import React from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  ArrayInput,
  SimpleFormIterator,
  FormDataConsumer,
  NumberInput,
  required,
  maxLength,
  minValue,
  maxValue,
  useEditContext,
} from 'react-admin';
import ImageInput from '../../../commonComponents/UploadInputs/ImageInput';
import { uploadTypes } from '../../../config/constants';
import CrosswordGridPreview from './CrosswordGridPreview';

const statusChoices = [
  { id: 'active', name: 'Active' },
  { id: 'inactive', name: 'Inactive' },
];

const directionChoices = [
  { id: 'horizontal', name: 'Horizontal →' },
  { id: 'vertical', name: 'Vertical ↓' },
];

const CrosswordsEdit = (props) => {
  const { record } = useEditContext(props);

  const maxCharacters = (value) =>
    Array.isArray(value) && value.length > 10 ? 'Maximum 10 characters allowed' : undefined;

  return (
    <Edit
      undoable={false}
      transform={(data) => ({
        ...data,
        items: Array.isArray(data.items)
          ? data.items.map((item, index) => ({ ...item, position: index + 1 }))
          : data.items,
      })}
      {...props}
    >
      <SimpleForm key={record ? record.id : 'crosswords-edit'}>
        <TextInput source="id" disabled />
        <TextInput label="Title" source="title" validate={[required(), maxLength(100)]} fullWidth />
        <SelectInput source="status" choices={statusChoices} validate={required()} />

        {/* Crosswords items (max 8) */}
        <ArrayInput source="items" label="Crosswords (max 8)">
          <SimpleFormIterator>
            {/* Background & credit */}
            <FormDataConsumer>
              {({ getSource }) => (
                <ImageInput
                  label="Background image"
                  imageInputSource={getSource('background_image')}
                  uploadType={uploadTypes.allUploads}
                />
              )}
            </FormDataConsumer>
            <FormDataConsumer>
              {({ getSource }) => (
                <ImageInput
                  label="Logo image"
                  imageInputSource={getSource('logo_img')}
                  uploadType={uploadTypes.allUploads}
                />
              )}
            </FormDataConsumer>
            <NumberInput label="Credit amount" source="credit_amount" validate={[required(), minValue(0)]} />

            {/* Characters bank (string[]) */}
            <ArrayInput source="characters" label="Character Bank" defaultValue={[]} validate={maxCharacters}>
              <SimpleFormIterator>
                <TextInput
                  label="Character"
                  source="."
                  parse={(v) => (v ? String(v).toUpperCase().slice(0, 1) : v)}
                  validate={[required(), maxLength(1)]}
                  helperText="1 character; suggested 10 total"
                />
              </SimpleFormIterator>
            </ArrayInput>

            {/* Live grid preview for this crossword */}
            <FormDataConsumer>
              {({ scopedFormData }) => <CrosswordGridPreview items={(scopedFormData && scopedFormData.words) || []} />}
            </FormDataConsumer>

            <ArrayInput source="words" label="Words (placements)" defaultValue={[]}>
              <SimpleFormIterator>
                <TextInput
                  label="Word"
                  source="word"
                  validate={[required(), maxLength(10)]}
                  helperText="The word to place in the grid (max 10 characters)"
                />
                <NumberInput
                  label="Start Row"
                  source="startRow"
                  validate={[required(), minValue(1), maxValue(10)]}
                  helperText="Row position (1-10)"
                />
                <NumberInput
                  label="Start Column"
                  source="startCol"
                  validate={[required(), minValue(1), maxValue(10)]}
                  helperText="Column position (1-10)"
                />
                <SelectInput
                  source="direction"
                  choices={directionChoices}
                  validate={[required()]}
                  defaultValue="horizontal"
                  helperText="Direction the word extends from start position"
                  label="Direction"
                />
              </SimpleFormIterator>
            </ArrayInput>
          </SimpleFormIterator>
        </ArrayInput>
      </SimpleForm>
    </Edit>
  );
};

export default CrosswordsEdit;
